import React from 'react';
import { Eye, FileText, RefreshCw, ShieldCheck, Users } from 'lucide-react';
import {
  AcceptedPrivacyUser,
  PendingPrivacyUser,
  PrivacyComplianceSummary,
  PrivacyConsentDetail,
  PrivacyNoticeVersionRecord,
  privacyService,
} from '../../services/privacyService';
import DismissibleError from '../DismissibleError';
import { EmptyTableState, FormModal, RecordsTableCard, StatusBadge } from '../records';

type PrivacyTab = 'accepted' | 'pending' | 'notices';

const formatDateTime = (input?: string | null): string => {
  if (!input) return '-';
  const date = new Date(input);
  return Number.isNaN(date.getTime()) ? input : date.toLocaleString();
};

const value = (input?: string | number | null, fallback = '-'): string =>
  input === null || input === undefined || input === '' ? fallback : String(input);

const DataPrivacyCompliance: React.FC = () => {
  const [summary, setSummary] = React.useState<PrivacyComplianceSummary | null>(null);
  const [acceptedUsers, setAcceptedUsers] = React.useState<AcceptedPrivacyUser[]>([]);
  const [pendingUsers, setPendingUsers] = React.useState<PendingPrivacyUser[]>([]);
  const [notices, setNotices] = React.useState<PrivacyNoticeVersionRecord[]>([]);
  const [selectedConsent, setSelectedConsent] = React.useState<PrivacyConsentDetail | null>(null);
  const [selectedNotice, setSelectedNotice] = React.useState<PrivacyNoticeVersionRecord | null>(null);
  const [activeTab, setActiveTab] = React.useState<PrivacyTab>('accepted');
  const [loading, setLoading] = React.useState(true);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [nextSummary, nextAccepted, nextPending, nextNotices] = await Promise.all([
        privacyService.getComplianceSummary(),
        privacyService.getAcceptedUsers(),
        privacyService.getPendingUsers(),
        privacyService.getNoticeVersions(),
      ]);
      setSummary(nextSummary);
      setAcceptedUsers(nextAccepted);
      setPendingUsers(nextPending);
      setNotices(nextNotices);
    } catch {
      setError('Data Privacy compliance evidence is temporarily unavailable.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);

  const openConsentDetail = async (id: number) => {
    setDetailLoading(true);
    setError('');
    try {
      setSelectedConsent(await privacyService.getConsentDetail(id));
    } catch {
      setError('Consent detail is temporarily unavailable.');
    } finally {
      setDetailLoading(false);
    }
  };

  const summaryCards = [
    { label: 'Active DPA Version', value: summary?.active_notice_version || 'None', note: 'Current required agreement', icon: ShieldCheck, tone: 'blue' },
    { label: 'Accepted Users', value: summary?.accepted_users_count ?? 0, note: `${summary?.total_active_users_count ?? 0} active users`, icon: Users, tone: 'green' },
    { label: 'Pending Acceptance', value: summary?.pending_users_count ?? 0, note: 'Active users still required', icon: Users, tone: 'neutral' },
    { label: 'Latest Acceptance', value: formatDateTime(summary?.latest_acceptance_timestamp), note: 'Most recent proof timestamp', icon: FileText, tone: 'purple' },
    { label: 'Compliance Rate', value: `${summary?.compliance_rate ?? 0}%`, note: 'Accepted / active users', icon: ShieldCheck, tone: 'green' },
  ];

  return (
    <div className="admin-dashboard privacy-compliance">
      <div className="page-heading">
        <div>
          <h1>Data Privacy Compliance</h1>
          <p>Read-only evidence for active DPA acknowledgement, pending users, and notice versions.</p>
        </div>
        <div className="actions">
          <a className="btn btn-secondary" href="/privacy-agreement" target="_blank" rel="noreferrer"><FileText size={14} />View Agreement</a>
          <button className="btn btn-secondary" type="button" onClick={load}><RefreshCw size={14} />Refresh</button>
        </div>
      </div>

      {error && <DismissibleError message={error} onDismiss={() => setError('')} onRetry={load} />}

      <div className="stats-grid privacy-stats">
        {summaryCards.map((card) => {
          const Icon = card.icon;
          return (
            <div className="stat-card" key={card.label}>
              <span className={`metric-icon ${card.tone}`}><Icon size={20} /></span>
              <div><span className="stat-label">{card.label}</span><strong className="stat-number">{card.value}</strong><span className="stat-note">{card.note}</span></div>
            </div>
          );
        })}
      </div>

      <div className="privacy-tabs" role="tablist" aria-label="Privacy compliance sections">
        <button className={activeTab === 'accepted' ? 'active' : ''} onClick={() => setActiveTab('accepted')} type="button">Accepted Users</button>
        <button className={activeTab === 'pending' ? 'active' : ''} onClick={() => setActiveTab('pending')} type="button">Pending Users</button>
        <button className={activeTab === 'notices' ? 'active' : ''} onClick={() => setActiveTab('notices')} type="button">Notice Versions</button>
      </div>

      {activeTab === 'accepted' && (
        <RecordsTableCard title="Accepted Users" description="Users with non-withdrawn consent for the active Data Privacy Agreement." meta={loading ? 'Loading' : `${acceptedUsers.length} accepted`}>
          <table className="records-table">
            <thead><tr><th>User</th><th>Role</th><th>Department</th><th>Notice Version</th><th>Accepted At</th><th>Status</th><th>Action</th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7}><div className="loading-state"><span className="loading-mark" />Loading accepted users...</div></td></tr>
              ) : acceptedUsers.length === 0 ? (
                <tr><td colSpan={7}><EmptyTableState title="No accepted users" description="No active users have accepted the active DPA yet." /></td></tr>
              ) : acceptedUsers.map((item) => (
                <tr key={item.id}>
                  <td>{item.username}</td>
                  <td>{value(item.role)}</td>
                  <td>{value(item.department)}</td>
                  <td>{item.notice_version}</td>
                  <td>{formatDateTime(item.accepted_at)}</td>
                  <td><StatusBadge status="accepted" label={item.status} /></td>
                  <td><button className="btn btn-secondary" type="button" disabled={detailLoading} onClick={() => openConsentDetail(item.id)}><Eye size={14} />View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </RecordsTableCard>
      )}

      {activeTab === 'pending' && (
        <RecordsTableCard title="Pending Users" description="Active users who have not accepted the active DPA version." meta={loading ? 'Loading' : `${pendingUsers.length} pending`}>
          <table className="records-table">
            <thead><tr><th>User</th><th>Role</th><th>Department</th><th>Account Status</th><th>Last Login</th><th>Required Notice Version</th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6}><div className="loading-state"><span className="loading-mark" />Loading pending users...</div></td></tr>
              ) : pendingUsers.length === 0 ? (
                <tr><td colSpan={6}><EmptyTableState title="No pending users" description="Every active user has accepted the active DPA." /></td></tr>
              ) : pendingUsers.map((item) => (
                <tr key={item.user_id}>
                  <td>{item.username}</td>
                  <td>{value(item.role)}</td>
                  <td>{value(item.department)}</td>
                  <td><StatusBadge status={item.account_status} label={item.account_status} /></td>
                  <td>{formatDateTime(item.last_login)}</td>
                  <td>{value(item.required_notice_version)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </RecordsTableCard>
      )}

      {activeTab === 'notices' && (
        <RecordsTableCard title="Privacy Notice Versions" description="Read-only DPA versions configured in the system." meta={loading ? 'Loading' : `${notices.length} version${notices.length === 1 ? '' : 's'}`}>
          <table className="records-table">
            <thead><tr><th>Version</th><th>Title</th><th>Effective Date</th><th>Active</th><th>Created At</th><th>Action</th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6}><div className="loading-state"><span className="loading-mark" />Loading notice versions...</div></td></tr>
              ) : notices.length === 0 ? (
                <tr><td colSpan={6}><EmptyTableState title="No notice versions" description="No Data Privacy Agreement versions are configured." /></td></tr>
              ) : notices.map((notice) => (
                <tr key={notice.id}>
                  <td>{notice.version}</td>
                  <td>{notice.title}</td>
                  <td>{notice.effective_date}</td>
                  <td><StatusBadge status={notice.is_active ? 'active' : 'inactive'} label={notice.is_active ? 'Active' : 'Inactive'} /></td>
                  <td>{formatDateTime(notice.created_at)}</td>
                  <td><button className="btn btn-secondary" type="button" onClick={() => setSelectedNotice(notice)}><Eye size={14} />View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </RecordsTableCard>
      )}

      {selectedConsent && (
        <FormModal open title="Consent Evidence" description="Read-only proof of Data Privacy Agreement acknowledgement." onClose={() => setSelectedConsent(null)} closeLabel="Close consent evidence" size="lg">
          <div className="detail-grid governance-detail-grid">
            {[
              ['User', selectedConsent.username],
              ['Full Name', selectedConsent.full_name],
              ['Email', selectedConsent.email],
              ['Role', selectedConsent.role],
              ['Department', selectedConsent.department],
              ['Notice Version', selectedConsent.notice_version],
              ['Accepted At', formatDateTime(selectedConsent.accepted_at)],
              ['IP Address', selectedConsent.ip_address],
              ['User Agent', selectedConsent.user_agent],
              ['Withdrawn', selectedConsent.is_withdrawn ? 'Yes' : 'No'],
              ['Withdrawn At', formatDateTime(selectedConsent.withdrawn_at)],
            ].map(([label, fieldValue]) => (
              <div key={label}><span>{label}</span><strong>{value(fieldValue)}</strong></div>
            ))}
            <div className="full-width">
              <span>Consent Text Snapshot</span>
              <pre className="governance-detail-pre">{selectedConsent.consent_text_snapshot}</pre>
            </div>
          </div>
        </FormModal>
      )}

      {selectedNotice && (
        <FormModal open title={`Privacy Notice v${selectedNotice.version}`} description={selectedNotice.title} onClose={() => setSelectedNotice(null)} closeLabel="Close privacy notice" size="lg">
          <div className="detail-grid governance-detail-grid">
            <div><span>Version</span><strong>{selectedNotice.version}</strong></div>
            <div><span>Effective Date</span><strong>{selectedNotice.effective_date}</strong></div>
            <div><span>Active</span><strong>{selectedNotice.is_active ? 'Yes' : 'No'}</strong></div>
            <div><span>Created At</span><strong>{formatDateTime(selectedNotice.created_at)}</strong></div>
            <div className="full-width"><span>Notice Content</span><pre className="governance-detail-pre">{selectedNotice.content}</pre></div>
          </div>
        </FormModal>
      )}
    </div>
  );
};

export default DataPrivacyCompliance;
